<template>
  <div class="owner-exit-room-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('ownerExitRoom.page.title')" :subtitle="$t('ownerExitRoom.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="selectOwner">
          {{ $t('common.select') }}
        </el-button>
        <el-button size="mini" icon="el-icon-back" @click="goBack">
          {{ $t('common.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- 业主信息 -->
    <div class="ds-panel">
      <ds-toolbar :total="null" :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('ownerExitRoom.ownerInfo') }}</span>
        </template>
      </ds-toolbar>

      <div class="ds-panel__body">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="8">
            <div class="ds-info-item">
              <span class="ds-info-item__label">{{ $t('ownerExitRoom.name') }}</span>
              <span class="ds-info-item__value">{{ ownerExitRoomInfo.name || '-' }}</span>
            </div>
          </el-col>
          <el-col :xs="24" :sm="8">
            <div class="ds-info-item">
              <span class="ds-info-item__label">{{ $t('ownerExitRoom.idCard') }}</span>
              <span class="ds-info-item__value ds-num">{{ ownerExitRoomInfo.idCard || '-' }}</span>
            </div>
          </el-col>
          <el-col :xs="24" :sm="8">
            <div class="ds-info-item">
              <span class="ds-info-item__label">{{ $t('ownerExitRoom.phone') }}</span>
              <span class="ds-info-item__value ds-num">{{ ownerExitRoomInfo.link || '-' }}</span>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 业主房屋 -->
    <div class="ds-panel">
      <ds-toolbar :total="ownerExitRoomInfo.rooms.length" :show-column-setting="false" :show-density="false"
        :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('ownerExitRoom.ownerRoom') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="ownerExitRoomInfo.rooms" :empty-description="$t('ownerExitRoom.page.emptyRoom')"
        border @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="50" align="center" />
        <el-table-column prop="roomName" :label="$t('ownerExitRoom.room')" align="center" width="180">
          <template slot-scope="scope">
            <span class="ds-num">
              {{ scope.row.floorNum }}-{{ scope.row.unitNum }}-{{ scope.row.roomNum }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="builtUpArea" :label="$t('ownerExitRoom.builtUpArea')" align="right" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.builtUpArea }} {{ $t('ownerExitRoom.squareMeter') }}</span>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <!-- 费用信息 -->
    <div v-if="ownerExitRoomInfo.fees && ownerExitRoomInfo.fees.length > 0" class="ds-panel">
      <ds-toolbar :total="ownerExitRoomInfo.fees.length" :show-column-setting="false" :show-density="false"
        :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('ownerExitRoom.feeInfo') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="ownerExitRoomInfo.fees" border>
        <el-table-column prop="feeName" :label="$t('ownerExitRoom.feeItem')" align="left" min-width="140"
          show-overflow-tooltip />
        <el-table-column prop="payerObjName" :label="$t('ownerExitRoom.room')" align="center" min-width="140"
          show-overflow-tooltip />
        <el-table-column prop="amountOwed" :label="$t('ownerExitRoom.arrears')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.amountOwed) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" :label="$t('ownerExitRoom.startTime')" align="center" width="160" />
        <el-table-column prop="deadlineTime" :label="$t('ownerExitRoom.endTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.deadlineTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('ownerExitRoom.feeStatus')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveFeeState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <!-- 提交按钮 -->
    <div class="submit-btn">
      <el-button type="primary" @click="saveOwnerExitRoomInfo">
        <i class="el-icon-check"></i>&nbsp;{{ $t('common.submit') }}
      </el-button>
    </div>

    <!-- 子组件 -->
    <search-owner ref="searchOwner" @chooseOwner="handleChooseOwner"></search-owner>
    <select-fee-config ref="selectFeeConfig"></select-fee-config>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { ownerExitRoom, queryRoomsByOwner, listFee } from '@/api/room/ownerExitRoomApi'
import SearchOwner from '@/components/owner/SearchOwner'
import SelectFeeConfig from '@/components/room/selectFeeConfig'
import { resolveStatus } from '@/utils/statusMap'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'OwnerExitRoomList',
  components: {
    SearchOwner,
    SelectFeeConfig,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag
  },
  data() {
    return {
      ownerExitRoomInfo: {
        name: '',
        link: '',
        ownerId: '',
        idCard: '',
        rooms: [],
        selectRooms: [],
        batchFees: [],
        fees: [],
        communityId: ''
      }
    }
  },
  created() {
    this.ownerExitRoomInfo.communityId = getCommunityId()
  },
  methods: {
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value) {
      return formatMoney(value)
    },
    /**
     * 费用状态语义类型：欠费（2008001）为危险色，其余按文案语义推断。
     */
    resolveFeeState(row) {
      if (!row) return 'muted'
      if (row.state === '2008001') return 'danger'
      return resolveStatus(row.state, { text: row.stateName || '' }).type
    },
    selectOwner() {
      this.$refs.searchOwner.open()
    },
    goBack() {
      this.$router.go(-1)
    },
    handleChooseOwner(owner) {
      this.ownerExitRoomInfo = {
        ...this.ownerExitRoomInfo,
        name: owner.name,
        link: owner.link,
        ownerId: owner.ownerId,
        idCard: owner.idCard
      }
      this.listRoom()
    },
    handleSelectionChange(val) {
      this.ownerExitRoomInfo.selectRooms = val.map(item => item.roomId)
      this.dealFees()
    },
    async saveOwnerExitRoomInfo() {
      if (!this.ownerExitRoomInfo.ownerId) {
        this.$message.error(this.$t('ownerExitRoom.validate.ownerRequired'))
        return
      }

      if (!this.ownerExitRoomInfo.selectRooms || this.ownerExitRoomInfo.selectRooms.length < 1) {
        this.$message.error(this.$t('ownerExitRoom.validate.roomRequired'))
        return
      }

      const hasUnpaidFee = this.ownerExitRoomInfo.fees.some(fee => fee.state === '2008001')
      if (hasUnpaidFee) {
        this.$message.error(this.$t('ownerExitRoom.validate.unpaidFee'))
        return
      }

      try {
        const res = await ownerExitRoom(this.ownerExitRoomInfo)
        if (res.code === 0) {
          this.$message.success(this.$t('common.operationSuccess'))
          this.goBack()
        } else {
          this.$message.error(res.msg)
        }
      } catch (error) {
        this.$message.error(this.$t('common.submitFailed'))
      }
    },
    async listRoom() {
      try {
        const params = {
          ownerId: this.ownerExitRoomInfo.ownerId,
          communityId: this.ownerExitRoomInfo.communityId,
          page: 1,
          row: 100
        }
        const res = await queryRoomsByOwner(params)
        this.ownerExitRoomInfo.rooms = res.rooms
        this.ownerExitRoomInfo.selectRooms = res.rooms.map(room => room.roomId)
        this.loadBatchFees()
      } catch (error) {
        console.error('获取房屋列表失败:', error)
      }
    },
    async loadBatchFees() {
      try {
        const params = {
          communityId: this.ownerExitRoomInfo.communityId,
          ownerId: this.ownerExitRoomInfo.ownerId,
          payerObjType: '3333',
          state: '2008001',
          page: 1,
          row: 500
        }
        const res = await listFee(params)
        this.ownerExitRoomInfo.batchFees = res.fees || []
        this.dealFees()
      } catch (error) {
        console.error('获取费用列表失败:', error)
      }
    },
    dealFees() {
      const selectedRoomIds = this.ownerExitRoomInfo.selectRooms
      this.ownerExitRoomInfo.fees = this.ownerExitRoomInfo.batchFees.filter(fee =>
        selectedRoomIds.includes(fee.payerObjId)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.owner-exit-room-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-md);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__body {
    padding: var(--ds-spacing-md) var(--ds-spacing-lg);
  }
}

/* 键值信息项 */
.ds-info-item {
  display: flex;
  align-items: baseline;
  gap: var(--ds-spacing-sm);
  padding: var(--ds-spacing-xs) 0;

  &__label {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
    flex-shrink: 0;
  }

  &__value {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-800);
    font-weight: 500;
  }
}

.submit-btn {
  margin-top: var(--ds-spacing-lg);
  text-align: right;
}
</style>